import { useForm } from "react-hook-form"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select"
import { Textarea } from "@components/ui/textarea"
import {
  useGetAccountStatusesQuery,
  useGetAccountStatusReasonsQuery,
} from "@apis/shared/useShared"
import { useUpdateAccountStatusMutation } from "@apis/accounts/useAccounts"
import { accountKeys } from "@apis/accounts/queryKeys"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import { Loader2 } from "lucide-react"

import { Button } from "@components/ui/button"
import type { IUpdateAccountStatusRequest } from "@apis/accounts/types"
import { useDrawer } from "@contexts/DrawerContext"

export const AccountDuplicateStatusForm = ({
  account_id,
}: {
  account_id: string
}) => {
  const updateAccountStatusMutation = useUpdateAccountStatusMutation()
  const queryClient = useQueryClient()
  const { closeDrawer } = useDrawer()

  const { data: accountStatuses, isPending: isAccountStatusesPending } =
    useGetAccountStatusesQuery()
  const {
    data: accountStatusReasons,
    isPending: isAccountStatusReasonsPending,
  } = useGetAccountStatusReasonsQuery()
  const duplicateStatus = accountStatuses?.find(
    (status) => status.value === "DUPLICATE",
  )
  const aleadyActiveReason = accountStatusReasons?.find(
    (reason) => reason.value === "ACCOUNT_ALREADY_ACTIVE",
  )

  const EMPTY_ACCOUNT_STATUS_FORM = {
    account_status_code: "",
    account_status_reason_code: "",
    account_status_message: "",
  }

  const serializeAccountStatusFormData =
    isAccountStatusesPending || isAccountStatusReasonsPending
      ? undefined
      : {
          account_status_code: duplicateStatus?.value || "",
          account_status_reason_code: aleadyActiveReason?.value || "",
          account_status_message: "",
        }

  const form = useForm<IUpdateAccountStatusRequest>({
    mode: "onTouched",
    defaultValues: EMPTY_ACCOUNT_STATUS_FORM,
    values: serializeAccountStatusFormData,
  })

  const { isDirty, isValid } = form.formState

  const handleSubmit = async (data: IUpdateAccountStatusRequest) => {
    updateAccountStatusMutation.mutate(
      {
        account_id: account_id,
        request: data,
      },
      {
        onSuccess: () => {
          toast.success("Successfully changed account status!")
          form.reset()
          queryClient.invalidateQueries({
            queryKey: accountKeys.detail(account_id),
          })
          closeDrawer()
        },
        onError: (error) =>
          toast.error(`Error changing account status! ${error}`),
      },
    )
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="tw:grid tw:grid-cols-12 tw:items-baseline tw:gap-4"
      >
        <div className="tw:col-span-12 tw:md:col-span-6">
          <FormField
            control={form.control}
            name="account_status_code"
            rules={{ required: "Account status is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Account Status</FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value)
                  }}
                  value={field.value}
                  disabled
                >
                  <FormControl className="tw:w-full">
                    <SelectTrigger>
                      <SelectValue placeholder="Select Account Status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {accountStatuses?.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:col-span-12 tw:md:col-span-6">
          <FormField
            control={form.control}
            name="account_status_reason_code"
            rules={{ required: "Account status reason is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status Reason</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                  disabled
                >
                  <FormControl className="tw:w-full">
                    <SelectTrigger>
                      <SelectValue placeholder="Select Reason" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {accountStatusReasons?.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:col-span-12">
          <FormField
            control={form.control}
            name="account_status_message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Message</FormLabel>
                <FormControl>
                  <Textarea
                    className="tw:field-sizing-content"
                    placeholder="Enter a detailed message about the status change"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:col-span-12 tw:justify-self-end">
          <Button
            type="submit"
            disabled={
              updateAccountStatusMutation.isPending || !isDirty || !isValid
            }
            className="tw:flex-1"
          >
            {updateAccountStatusMutation.isPending ? (
              <>
                <Loader2 className="tw:animate-spin" />
                <span>Updating...</span>
              </>
            ) : (
              <span>Update Status</span>
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
